import { Component, OnInit } from '@angular/core';
import { AlertifyService } from 'src/app/_services/alertify.service';
import { WidgetServiceService } from 'src/app/_services/widget-service.service';
import { Widget } from 'src/app/_models/widget';

interface Entity extends Widget {
  // jsonTestData kept for compatibility with the previous mock data
  jsonTestData: string;
}

@Component({
  selector: 'app-widget-home',
  templateUrl: './widget-home.component.html',
  styleUrls: ['./widget-home.component.css']
})
export class WidgetHomeComponent implements OnInit {

  records: Entity[] = [];
  currentIndex: number = -1;
  currentRecord: Entity; // inicializado en ngOnInit para evitar orden de creación

  showForm = false; // controla visibilidad del formulario

  // preview modal state
  showPreview = false;
  previewResult = '';
  previewFormat: 'html' | 'xml' = 'html';

  // opciones de ejemplo para combo
  applicationOptions = [
    { id: '9ee5c0b0-0fcc-4865-a003-fefd27582201', label: 'Principal' },
    { id: 'aaaaaaaa-aaaa-aaaa-aaaa-aaaaaaaaaaaa', label: 'Secundaria' }
  ];
  roleOptions = ['Admin', 'User', 'Guest'];

  constructor(
    private _alertify: AlertifyService,
    private _widgetService: WidgetServiceService
  ) { }

  ngOnInit(): void {
    // inicializar registro vacío primero pero no mostrar formulario
    this.currentRecord = this.newEntity();
    this.showForm = false;

    // --- fetch real data from API ------------------------------------------------
    this._widgetService.getAll().subscribe(
      (widgets: Widget[]) => {

         console.log("Widgets loaded from API: ", widgets);

        // adapt Widget to Entity if extra fields are required
        this.records = widgets.map(w => ({ ...w } as any));
      },
      err => {
        console.error('Failed to load widgets', err);
        this._alertify.error('No se pudieron cargar los widgets.');
        // fallback to mock data if desired
        this.populateMock();
      }
    );

  }

  private newEntity(): Entity {
    return {
      id: 0,
      name: '',
      displayName: '',
      description: '',
      icon: '',
      applicationId: this.applicationOptions && this.applicationOptions.length
        ? this.applicationOptions[0].id
        : '',
      role: 'Admin',
      spName: '',
      isReady: false,
      template: '',
      jsonTestData: ''
    };
  }

  /** helper that populates the component with static mock data when the API call fails */
  private populateMock() {
    this.records = [
      {
        id: 1,
        name: 'First',
        displayName: 'Primer registro',
        description: 'Descripción del primero',
        icon: 'nc-chat-33',
        applicationId: this.applicationOptions[0].id,
        role: 'Admin',
        spName: 'sp_first',
        isReady: false,
        template: '',
        jsonTestData: ''
      },
      {
        id: 2,
        name: 'Second',
        displayName: 'Segundo registro',
        description: 'Descripción del segundo',
        icon: 'nc-bell-55',
        applicationId: this.applicationOptions[1].id,
        role: 'User',
        spName: 'sp_second',
        isReady: true,
        template: '{"foo":"bar"}',
        jsonTestData: ''
      }
    ];
  }

  loadRecord(index: number) {
    if (index < 0 || index >= this.records.length) { return; }
    this.currentIndex = index;
    // clonar para no modificar directamente la lista
    this.currentRecord = { ...this.records[index] };
    console.log("Current Record: ", this.currentRecord);
    this.showForm = true;
  }

  prev() {
    if (this.currentIndex > 0) {
      this.loadRecord(this.currentIndex - 1);
    } else {
      this._alertify.message('Ya estás en el primer registro');
    }
  }

  next() {
    if (this.currentIndex < this.records.length - 1) {
      this.loadRecord(this.currentIndex + 1);
    } else {
      this._alertify.message('Ya estás en el último registro');
    }
  }

  addNew() {
    this.currentRecord = this.newEntity();
    this.currentIndex = -1;
    this.showForm = true;
  }

  cancel() {
    // ocultar el formulario y limpiar registro
    this.showForm = false;
    this.currentRecord = this.newEntity();
  }

  private removeLocalRecord() {
    this.records.splice(this.currentIndex, 1);
    if (this.records.length) {
      const nextIndex = Math.min(this.currentIndex, this.records.length - 1);
      this.loadRecord(nextIndex);
    } else {
      this.currentRecord = this.newEntity();
      this.currentIndex = -1;
    }
  }

  save() {
    // store the user-entered json data so we can reapply it after the server
    // returns the canonical widget object (which doesn't carry that field).
    const userJson = this.currentRecord.jsonTestData || '';

    if (this.currentRecord.id === 0) {
      // agregar
      this._widgetService.create(this.currentRecord).subscribe(
        (created: Widget) => {
          const rec = { ...created, jsonTestData: userJson } as any;
          this.records.push(rec);
          this.currentIndex = this.records.length - 1;
          this.currentRecord = { ...rec };
          this._alertify.success('Registro agregado');
        },
        err => {
          console.error('Error creating widget', err);
          this._alertify.error('No se pudo agregar el widget');
        }
      );
    } else {
      // actualizar
      if (this.currentIndex >= 0) {
        this._widgetService.update(this.currentRecord.id, this.currentRecord).subscribe(
          (updated: Widget) => {
            const rec = { ...updated, jsonTestData: userJson } as any;
            this.records[this.currentIndex] = rec;
            this.currentRecord = { ...rec };
            this._alertify.success('Registro actualizado');
          },
          err => {
            console.error('Error updating widget', err);
            this._alertify.error('No se pudo actualizar el widget');
          }
        );
      }
    }
  }

  /** open preview modal by calling transform endpoint */
  openPreview() {
    if (!this.currentRecord || this.currentRecord.id === 0) {
      // must have an existing widget to transform
      this._alertify.message('Debe guardar el widget antes de previsualizar');
      return;
    }

    const payload = {
      format: this.previewFormat,
      jsonData: this.currentRecord.jsonTestData || ''
    };

    this._widgetService.transform(this.currentRecord.id, payload).subscribe(
      res => {
        this.previewResult = res;
        this.showPreview = true;
      },
      err => {
        console.error('Transform error', err);
        this._alertify.error('No se pudo generar la vista previa');
      }
    );
  }

  closePreview() {
    this.showPreview = false;
    this.previewResult = '';
  }

  delete() {
    if (this.currentIndex < 0 || this.currentIndex >= this.records.length) {
      return;
    }

    this._alertify.confirm('¿Desea eliminar este registro?', () => {
      const id = this.records[this.currentIndex].id;
      // attempt server delete but fall back to local removal if endpoint unavailable
      this._widgetService.delete(id).subscribe(
        () => {
          this.removeLocalRecord();
          this._alertify.success('Registro eliminado');
        },
        err => {
          console.warn('Server delete failed, removing locally', err);
          this.removeLocalRecord();
          this._alertify.success('Registro eliminado (no había servidor)');
        }
      );
    });
  }
}
